import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { isEqual } from 'lodash'
import { CircularProgress } from '@material-ui/core'
import { ForkliftWrapper } from './styles'
import Tab from '../../components/Tab'
import Table from '../../components/Table'
import EditForklift from './EditForklift/index'
import ForkliftDetail from './ForkliftDetail'
import { getForkliftList, clearState } from '../../features/forklift/forkliftSlice'
import { clearFileState } from '../../features/file/fileSlice'
import { getOrganizationList } from '../../features/organizations/organizationSlice'
import { forkliftColumnMap, forkliftGeneralTab } from '../../components/Table/columnNameMap'
import MultiSelect from '../../components/MultiSelect'
import Pagination from '../../components/Pagination'
import GeneralTab from '../../components/FilterGroup/forkliftGeneralTab'
import RegistrationTab from '../../components/FilterGroup/forkliftRegistrationTab'
import UpdateHistoryTab from '../../components/FilterGroup/forkliftUpdateHistoryTab'
import WeightSizeTab from '../../components/FilterGroup/forkliftWeightSizeTab'
import DetailVehicleTab from '../../components/FilterGroup/forkliftDetailVehicleTab'
import { BackToForkliftContractButton, CreateButton, HiddenButton, ExportExcelButton, DisplayButton } from '../../components/Button'
import i18n from 'i18next'

import { getForkliftCSVApi } from '../../features/forklift/forkliftAPI'
import { getDropDownIdApi } from '../../features/dropdown/dropdownAPI'
import Modal from '../../components/Modal/index'
import { userInfo } from './../../features/users/userInfoSlice'
import { useMounted } from '../../tools'
import ImportForklift from '../../components/ImportForklift'
import ImportForkliftFinance from '../../components/ImportDataForkliftFinance'
import { perPageOptions } from '../../common/const'
import { fieldNames as generalFieldNames } from '../../components/FilterGroup/Defines/forkliftGeneralTabDefine'
import { fieldNames as registrationFieldNames } from '../../components/FilterGroup/Defines/forkliftRegistrationTabDefine'
import { fieldNames as weightSizeFieldNames } from '../../components/FilterGroup/Defines/forkliftWeightSizeTabDefine'
import { fieldNames as detailFieldNames } from '../../components/FilterGroup/Defines/forkliftDetailTabDefine'

const fieldMapping = {
  annualInspection: 'annualInspectionDate', // 年次点検日（前回）
  lease: 'leaseEndDate', // リース期限
  rental: 'rentalEndDate', // レンタル期限
  inspection: 'inspectionExpireDate', // 車検証期限
  mandatoryInsurance: 'mandatoryInsuranceEndDate', // 自賠責保険期限
  voluntaryInsurance: 'voluntaryInsuranceEndDate'// 任意保険期限
}

function ForkliftManagement (props) {
  const isMounted = useMounted()
  const dispatch = useDispatch()
  const location = useLocation()
  const useFormMethods = useForm()
  const [selectedTab, setTab] = React.useState(location.state ? 1 : 0)
  const [advancedOrganization, setAdvancedOrganization] = React.useState({
    department: '',
    businessUnit: '',
    branchOffice: ''
  })
  const [sortOrder, setSortOrder] = React.useState()
  const [onDetail, setShowDetail] = React.useState(false)
  const [crtOrEdt, setCrtOrEdt] = React.useState('')
  const [onCreate, setShowCreate] = React.useState(false)
  const [selectedForklift, setSelectedForklift] = React.useState('')
  const [resetSwitch, setResetSwitch] = React.useState(false)
  const [Hidden, setHidden] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [perPage, setPerPage] = React.useState(1000)
  const [company, setCompany] = React.useState(location.state?.companyName ? location.state.companyName : '')
  const [branch, setBranch] = React.useState(location.state?.organizationId ? location.state.organizationId : [])
  const [branchAll, setBranchAll] = React.useState(false)
  const [searchBody, setSearchBody] = React.useState({})
  const [registrationTabDefault, setRegistrationTabDefault] = React.useState({})
  const [updateHistoryTabDefault, setUpdateHistoryTabDefault] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSearchClicked, setIsSearchClicked] = React.useState(false)
  const forkliftState = useSelector(state => state.forklift)
  const organizationState = useSelector(state => state.organization)
  const userState = useSelector(state => state.userInfo)
  React.useEffect(() => {
    // Get master data
    dispatch(clearState())
    dispatch(clearFileState())
    dispatch(getOrganizationList())
    dispatch(userInfo())
  }, [])

  React.useEffect(() => {
    const body = searchBody
    delete body.onSubmit
    setSearchBody(body)
    // To prevent render call api at first time page
    if (isMounted) {
      if (sortOrder) {
        dispatch(getForkliftList({ page: page, perPage: perPage, sort: { column: sortOrder.column, by: sortOrder.by }, body: body }))
      } else {
        dispatch(getForkliftList({ page: page, perPage: perPage, body: body }))
      }
    }
  }, [page])

  React.useEffect(() => {
    const body = searchBody
    delete body.onSubmit
    setSearchBody(body)
    if (isMounted) {
      if (forkliftState.list) {
        setPage(1)
        if (sortOrder) {
          dispatch(getForkliftList({ page: 1, perPage: perPage, sort: { column: sortOrder.column, by: sortOrder.by }, body: body }))
        } else {
          dispatch(getForkliftList({ page: 1, perPage: perPage, body: body }))
        }
      }
    }
  }, [perPage])

  React.useEffect(() => {
    // reset advanced organization
    setAdvancedOrganization({
      department: '',
      businessUnit: '',
      branchOffice: ''
    })
    if (selectedTab !== 1) {
      location.state = undefined
    }
    // reset default selected body
    setRegistrationTabDefault({})
    setUpdateHistoryTabDefault({})
  }, [selectedTab])

  React.useEffect(() => {
    // Re-search branch
    const body = searchBody
    body.onSubmit = 'onSubmit'
    if (branch.length > 0) {
      body.organizationId = { in: branch }
    } else {
      delete body.organizationId
    }
    setSearchBody(body)
    if (isMounted) {
      setPage(1) // fix sbs-350: reset page when company/branch changes
      dispatch(getForkliftList({ page: 1, perPage: perPage, body }))
    }
  }, [branch])
  React.useEffect(async () => {
    if (location.state) {
      const { organizationId, type, period } = location.state
      const status = await getDropDownIdApi({ categoryId: '40', name: '稼働' })
      const body = {
        status: status.data.id,
        [fieldMapping[type]]: { between: [period.from, period.to] }
      }
      if (organizationId.length > 0) {
        body.organizationId = { in: organizationId }
      }
      setRegistrationTabDefault(body)
      setUpdateHistoryTabDefault(body)
      setSearchBody(body)
      // clear history state
      window.history.replaceState(null, '')
      dispatch(getForkliftList({ page: 1, perPage: perPage, body }))
    }
  }, [])

  const onSearchSubmit = body => {
    setPage(1)
    if (!body.onSubmit) {
      body = buildItemRequest(body)
    }
    body = updateOrganizationBody(body)
    setSearchBody(body)
    setResetSwitch(!resetSwitch)
    dispatch(getForkliftList({ page: 1, perPage: perPage, body: body }))
    setIsSearchClicked(true)
  }

  const buildItemRequest = (data) => {
    const reqBody = {}
    reqBody.onSubmit = 'onSubmit'
    // 基本情報タブ
    for (const [, value] of Object.entries(generalFieldNames)) {
      if (value.isPlate) {
        // License plate
        if (data[value.name].address) {
          reqBody.licensePlateAddress = {
            ilike: `%${data[value.name].address}%`
          }
        }
        if (data[value.name].classNo) {
          reqBody.licensePlateClass = {
            ilike: `%${data[value.name].classNo}%`
          }
        }
        if (data[value.name].number) {
          reqBody.licensePlateNumber = data[value.name].number
        }
      } else if (value.range) {
        // From - To
        const queryItem = {}
        if (data[value.name].from) {
          queryItem.gte = data[value.name].from
        }
        if (data[value.name].to) {
          queryItem.lte = data[value.name].to
        }
        if (Object.keys(queryItem).length > 0) {
          reqBody[value.queryName] = queryItem
        }
      } else if (value.type === 'dropdown') {
        if (data[value.name] !== 'All') reqBody[value.queryName] = data[value.name]
      } else {
        // Others
        if (value.operator === 'ilike') {
          if (data[value.name]) {
            const reqValue = `%${data[value.name]}%`
            reqBody[value.queryName] = {
              [value.operator]: reqValue
            }
          }
        } else {
          if (data[value.name]) {
            reqBody[value.queryName] = data[value.name]
          }
        }
      }
    }

    // 登録情報タブ
    for (const key of Object.keys(registrationFieldNames)) {
      const fieldName = registrationFieldNames[key].name
      const type = registrationFieldNames[key].type

      switch (type) {
        case 'license':
          if (data[fieldName].length > 0) {
            switch (fieldName) {
              case 'licensePlateNumber':
                reqBody[fieldName] = data[fieldName]
                break
              default:
                reqBody[fieldName] = { ilike: `%${data[fieldName]}%` }
                break
            }
          }
          break
        case 'text':
          if (data[fieldName].length > 0) reqBody[fieldName] = { ilike: `%${data[fieldName]}%` }
          break
        case 'number':
        case 'date':
          if (data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { between: [data[fieldName].from, data[fieldName].to] }
          } else if (data[fieldName].from && !data[fieldName].to) {
            reqBody[fieldName] = { gte: data[fieldName].from }
          } else if (!data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { lte: data[fieldName].to }
          }
          break
        case 'month': {
          const date = new Date(data[fieldName].to)
          date.setMonth(date.getMonth() + 1, 0)
          if (data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { between: [data[fieldName].from + '-01', data[fieldName].to + `-${date.getDate()}`] }
          } else if (data[fieldName].from && !data[fieldName].to) {
            reqBody[fieldName] = { gte: data[fieldName].from + '-01' }
          } else if (!data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { lte: data[fieldName].to + `-${date.getDate()}` }
          }
          break
        }
        case 'dropdown':
          if (data[fieldName] !== 'All') reqBody[fieldName] = data[fieldName]
          break
      }
    }

    // 重量/サイズ情報タブ(常時表示検索項目)
    for (const key of Object.keys(weightSizeFieldNames)) {
      const fieldName = weightSizeFieldNames[key].name
      const type = weightSizeFieldNames[key].type

      switch (type) {
        case 'text':
          if (data[fieldName].length > 0) reqBody[fieldName] = { ilike: `%${data[fieldName]}%` }
          break
        case 'number':
        case 'month':
        case 'date':
          if (data[fieldName] && data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { between: [data[fieldName].from, data[fieldName].to] }
          } else if (data[fieldName] && data[fieldName].from && !data[fieldName].to) {
            reqBody[fieldName] = { gte: data[fieldName].from }
          } else if (data[fieldName] && !data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { lte: data[fieldName].to }
          }
          break
      }
    }

    // 車両情報詳細タブ
    for (const key of Object.keys(detailFieldNames)) {
      const fieldName = detailFieldNames[key].name
      const type = detailFieldNames[key].type

      switch (type) {
        case 'text':
          if (data[fieldName].length > 0) reqBody[fieldName] = { ilike: `%${data[fieldName]}%` }
          break
        case 'number':
        case 'month':
        case 'date':
          if (data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { between: [data[fieldName].from, data[fieldName].to] }
          } else if (data[fieldName].from && !data[fieldName].to) {
            reqBody[fieldName] = { gte: data[fieldName].from }
          } else if (!data[fieldName].from && data[fieldName].to) {
            reqBody[fieldName] = { lte: data[fieldName].to }
          }
          break
        case 'dropdown':
          if (data[fieldName] !== 'All') reqBody[fieldName] = data[fieldName]
          break
      }
    }

    // 追加検索条件項目
    // 基本情報タブ
    const selectedDepartment = data.advanceSearchInput_department
    const selectedBusinessUnit = data.advanceSearchInput_businessUnit
    const selectedBranchOffice = data.advanceSearchInput_branchOffice
    if (branchAll && (selectedDepartment || selectedBusinessUnit || selectedBranchOffice)) {
      const selectedOrganizations = organizationState.list.filter(item => item.company === company &&
        (!selectedDepartment || item.department === selectedDepartment) &&
        (!selectedBusinessUnit || item.businessUnit === selectedBusinessUnit) &&
        (!selectedBranchOffice || item.branchOffice === selectedBranchOffice)
      ).map(item => parseInt(item.id))

      reqBody.organizationId = { in: selectedOrganizations }
    }

    return reqBody
  }

  const updateOrganizationBody = body => {
    if (!('organizationId' in body) && branch.length > 0) {
      body.organizationId = { in: branch }
    }
    return body
  }

  const onExport = async () => {
    setIsLoading(true)
    const response = await getForkliftCSVApi({ body: searchBody })
    const bytes = Buffer.from(response.data.base64Data, 'base64')
    const blob = new Blob([bytes])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = i18n.t('FORKLIFT_FORKLIFT-LIST-EXPORT_DEFAULT-EXPORT-FILE-NAME_LABEL')
    link.click()
    link.remove()
    setIsLoading(false)
  }

  const tablist = [
    <Tab.TabItem key={1} name={i18n.t('VEHICLE_VEHICLE-LIST_GENERAL-TAB_LABEL')}><GeneralTab onSearchSubmit={onSearchSubmit} company={company} branchAll={branchAll} setAdvancedOrganization={setAdvancedOrganization} /></Tab.TabItem>,
    <Tab.TabItem key={2} name={i18n.t('VEHICLE_VEHICLE-LIST_REGISTRATINO-INFO-TAB_LABEL')}>{<RegistrationTab onSearchSubmit={onSearchSubmit} defaultValue={registrationTabDefault} />}</Tab.TabItem>,
    <Tab.TabItem key={3} name={i18n.t('VEHICLE_VEHICLE-LIST_WEIGHT-SIZE-INFO-TAB_LABEL')}>{<WeightSizeTab onSearchSubmit={onSearchSubmit} />}</Tab.TabItem>,
    <Tab.TabItem key={4} name={i18n.t('VEHICLE_VEHICLE-LIST_DETAILED-VEHICLE-INFO-TAB_LABEL')}>{<DetailVehicleTab onSearchSubmit={onSearchSubmit} />}</Tab.TabItem>,
    <Tab.TabItem key={5} name={i18n.t('VEHICLE_VEHICLE-LIST_UPDATE-HISTORY-INFO-TAB_LABEL')}>{<UpdateHistoryTab onSearchSubmit={onSearchSubmit} defaultValue={updateHistoryTabDefault} />}</Tab.TabItem>
  ]

  if (userState.email !== '' && !(userState.isEdit || userState.isSuperAdmin)) {
    const index = tablist.findIndex((i) => i.key === 5)
    tablist.splice(index, 1)
  }

  const sortHandle = (column, by) => {
    setSortOrder(by === 'def' ? null : { column, by })
    const body = searchBody
    delete body.onSubmit
    setSearchBody(body)
    const searchCondition = { page, perPage: perPage, body: body }
    if (by !== 'def') {
      searchCondition.sort = { column, by }
    }
    dispatch(getForkliftList(searchCondition))
  }

  const handlePageChange = pagePag => {
    setPage(pagePag)
  }

  const handleCompanyChange = (e) => {
    setCompany(e[0].key)
    if (!e[0].key && isMounted) {
      setBranch([])
      setBranchAll(true)
    }
  }

  const handleBranchChange = (e, allSelected) => {
    if (e.length > 0) {
      const newBranchs = e.map(item => item.id)
      // Doesn't change state if newBranchs same as previous.
      if (!isEqual(newBranchs, branch)) {
        setBranch(newBranchs)
      }
    }
    setBranchAll(allSelected)
  }

  const handlePerPageChange = (e, allSelected) => {
    if (e.length > 0) {
      const newPerPage = perPageOptions.find(item => item.value === e)
      // Doesn't change state if newBranchs same as previous.
      if (!isEqual(newPerPage.key, perPage)) {
        setPerPage(newPerPage.key)
      }
    }
    // setBranchAll(allSelected)
  }

  const getCompanyOptions = () => {
    let companyOptions = [...new Set(organizationState.list.map(item => item.company))]
    companyOptions = companyOptions.map(item => { return { key: item, value: item } })
    return [{ key: '', value: i18n.t('COMMON_COMMON_DROPDOWN-OPTION-ALL_LABEL') }, ...companyOptions]
  }
  // UNIT test
  const onListClick = id => {
    setShowDetail(true)
    window.history.pushState({ urlPath: '/forklift' }, '', `/forkliftviewinfo/${id}`)
    setSelectedForklift(id)
  }
  // UNIT test
  const setMiddleDetailBack = bool => {
    window.history.pushState({ urlPath: '/forklift' }, '', '/forklift')
    setShowDetail(bool)
    setShowCreate(false)
    setCrtOrEdt('')
    dispatch(clearFileState())
  }
  // UNIT test
  const setMiddleCreateBack = state => {
    const body = searchBody
    delete body.onSubmit
    setSearchBody(body)
    /* istanbul ignore else */
    if (state === 'create') {
      window.history.pushState({ urlPath: '/forklift' }, '', '/forklift')
      setShowCreate(false)
    } else if (state === 'edit') {
      window.history.pushState({ urlPath: '/forklift' }, '', `/forkliftviewinfo/${selectedForklift}`)
      setShowDetail(true)
      setShowCreate(false)
    }
    dispatch(getForkliftList({ page: page, perPage: perPage, body: body }))
    setCrtOrEdt('')
  }

  const setMiddleCreate = bool => {
    window.history.pushState({ urlPath: '/forklift' }, '', '/forklift/create')
    setCrtOrEdt('create')
    setShowCreate(bool)
  }
  // UNIT test
  const setMiddleEdit = id => {
    window.history.pushState({ urlPath: '/forklift' }, '', `/forklifteditinfo/${id}/edit`)
    setCrtOrEdt('edit')
    setShowDetail(false)
    setShowCreate(true)
  }

  const getBranchOptions = () => {
    const { department, businessUnit, branchOffice } = advancedOrganization

    const selectedOrganizations = organizationState.list.filter(item => item.company === company &&
      (!department || item.department === department) &&
      (!businessUnit || item.businessUnit === businessUnit) &&
      (!branchOffice || item.branchOffice === branchOffice)
    )
    return selectedOrganizations
  }

  const countToPage = page * perPage < forkliftState.itemCount ? page * perPage : forkliftState.itemCount
  const startItem = forkliftState.itemCount > 0 ? 1 + ((page - 1) * perPage) : 0

  const tableColumn = (isSearchClicked && selectedTab === 0) ? forkliftGeneralTab : forkliftColumnMap[selectedTab]
  return (
    <>
      {onDetail && <ForkliftDetail history={props.history} onEdit={setMiddleEdit} onBack={setMiddleDetailBack} id={selectedForklift} />}
      {onCreate && <EditForklift state={crtOrEdt} history={props.history} onBack={setMiddleCreateBack} id={selectedForklift} />}
      <ForkliftWrapper show={onDetail || onCreate}>
        <Modal show={isLoading || forkliftState.load}><CircularProgress></CircularProgress></Modal>
        <section className="header">
          <div className="header-title">{i18n.t('FORKLIFT_FORKLIFT-LIST_PAGE-HEADER_LABEL')}</div>
          {location.state && <BackToForkliftContractButton state={location.state} />}

          <div className="header-actions">
            {Hidden && <DisplayButton onClick={() => setHidden(!Hidden)} />}
            {!Hidden && <HiddenButton onClick={() => setHidden(!Hidden)} />}
            {userState.isSuperAdmin && <ImportForkliftFinance />}
            {(userState.isEdit || userState.isSuperAdmin) && <ImportForklift />}
            {
              (userState.isEdit || userState.isSuperAdmin)
                ? <CreateButton data-testid="create-vehicle-button" onClick={() => setMiddleCreate(true)} />
                : ''
            }
            <div className="action-options">
              <label>{i18n.t('COMMON_COMMON_COMPANY-DROPDOWN_LABEL')}</label>
              <div>
                <MultiSelect
                  width="200px"
                  placeholder={i18n.t('COMMON_COMMON_DROPDOWN-PLEASE-SELECT_LABEL')}
                  options={getCompanyOptions()}
                  valueField="key"
                  labelField="value"
                  onChange={handleCompanyChange}
                  defaultValue={company}
                />
              </div>
            </div>
            <div className="action-options">
              <label>{i18n.t('COMMON_COMMON_BRANCH-DROPDOWN_LABEL')}</label>
              <div>
                <MultiSelect
                  disabled={!company}
                  width="200px"
                  placeholder={i18n.t('COMMON_COMMON_DROPDOWN-PLEASE-SELECT_LABEL')}
                  multi={true}
                  allItem={true}
                  options={getBranchOptions()}
                  valueField="id"
                  labelField="branch"
                  onChange={handleBranchChange}
                  // onAllSelectChange={handleAllSelectChange}
                  defaultValue={branch}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="tab" style={{ marginTop: '3px' }}>
          <FormProvider {...useFormMethods}>
            <Tab tabList={tablist} onTabChange={setTab} tab={selectedTab} hidden={Hidden}/>
          </FormProvider>
        </section>

        <section className="list">
          <div className="list-action-wrapper">
            <ExportExcelButton data-testid="export-button" onClick={onExport} {...forkliftState.itemCount <= 0 && { disabled: true }} />
            <div className="action-options">
              <select onChange={(e) => { handlePerPageChange(e.target.value) }} defaultValue='1000件' className="perpage-item">
                {
                  perPageOptions.map((item) => (
                      <option value={item.value} key={item.key}>{item.value}</option>
                  ))
                }
              </select>
            </div>
            <span>{i18n.t('COMMON_COMMON_PAGINATION-DISPLAY_LABEL', { page: startItem, countToPage, total: forkliftState.itemCount })}</span>
            {/* <span>Display {page} to {countToPage} out of {vehicleState.itemCount}</span> */}
            <Pagination page={page} count={Math.ceil(forkliftState.itemCount / perPage)} onChange={handlePageChange} />
          </div>
          {(!onDetail && !onCreate) && <Table borderCollapse={'separate'} onItemClick={onListClick} history={props.history} data={forkliftState.list} columnName={tableColumn} sortHandle={sortHandle} resetSwitch={resetSwitch} />}
        </section>

      </ForkliftWrapper>
    </>)
}

export default ForkliftManagement

ForkliftManagement.propTypes = {
  history: PropTypes.object
}
